'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Send, BookOpen, Loader2, AlertCircle, RefreshCw, Copy, Check, ExternalLink, FileText, Globe, HelpCircle } from 'lucide-react';
import Image from 'next/image';
import AnswerActions from '@/components/AnswerActions';
import AdminOverridesBanner from '@/components/AdminOverridesBanner';
import { createClient } from '@/lib/supabase/client';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isError?: boolean;
  isLimitReached?: boolean;
}

interface Citation {
  id: string;
  text: string;
  article: string;
  law: string;
  url: string;
  type: 'law' | 'pol' | 'egk' | 'aade' | 'other';
  sourceLabel: string;
  isUnverified?: boolean;
  isUnreachable?: boolean;
  isValidating?: boolean;
}

// Matches: (Άρθρο 15, Ν. 5246/2025) or (Άρθρο 15 παρ. 3, Ν. 4172/2013)
const LAW_CITATION_REGEX = /(?:Άρθρ[οα]\s[\d\w\s.\-–παρ.]+,\s*)?Ν\.?\s*(\d{4})\/(\d{4})/g;
// Matches: (ΠΟΛ. 1234/2024) or (ΠΟΛ 1234/2024)
const POL_CITATION_REGEX = /ΠΟΛ\.?\s*(\d+)\/(\d{4})/g;
// Matches: (ΕΓΚ. ΑΑΔΕ 1234/2024) or (Εγκύκλιος ΑΑΔΕ 1234/2024)
const EGK_CITATION_REGEX = /(?:ΕΓΚ\.|Εγκύκλιος)\s*(?:ΑΑΔΕ)?\s*(\d+)\/(\d{4})/g;
// Matches: (ΑΑΔΕ Α. 1234/2024) or (Απόφαση ΑΑΔΕ 1234/2024)
const AADE_CITATION_REGEX = /(?:ΑΑΔΕ\s*[Α-Ω]\.?|Α\.?\s*ΑΑΔΕ|Απόφαση\s*ΑΑΔΕ)\s*(\d+)\/(\d{4})/g;
// Matches: Πηγή: https://... or Πηγή: [UNVERIFIED] https://...
const SOURCE_URL_REGEX = /Πηγή:\s*(\[UNVERIFIED\]\s*)?(https?:\/\/[^\s\n\r\t,;)\]"]+)/gm;

const TRUSTED_DOMAINS = new Set([
  'aade.gr', 'www.aade.gr', 'mydata.aade.gr',
  'et.gr', 'www.et.gr', 'search.et.gr',
  'kodiko.gr', 'www.kodiko.gr', 'kodikoi.gr', 'www.kodikoi.gr',
  'hellenicparliament.gr', 'www.hellenicparliament.gr',
  'gsis.gr', 'www.gsis.gr',
  'gov.gr', 'www.gov.gr',
  'minfin.gr', 'www.minfin.gr',
  'hcmc.gr', 'www.hcmc.gr',
  'elte.org.gr', 'www.elte.org.gr',
  'sdoe.gr', 'www.sdoe.gr',
  'dpa.gr', 'www.dpa.gr',
  'efka.gov.gr', 'www.efka.gov.gr',
  'dypa.gov.gr', 'www.dypa.gov.gr',
  'e-forologia.gr', 'www.e-forologia.gr',
  'taxheaven.gr', 'www.taxheaven.gr',
  'oe-e.gr', 'www.oe-e.gr',
  'lsag.gr', 'www.lsag.gr',
  'adjustice.gr', 'www.adjustice.gr',
  'nsgel.gr', 'www.nsgel.gr',
  'bankofgreece.gr', 'www.bankofgreece.gr',
  'statistics.gr', 'www.statistics.gr',
  'mindigital.gr', 'www.mindigital.gr',
  'diavgeia.gov.gr', 'www.diavgeia.gov.gr',
  'nominal.gr', 'www.nominal.gr',
  'nomotelia.gr', 'www.nomotelia.gr',
  'qualex.gr', 'www.qualex.gr',
  'lawspot.gr', 'www.lawspot.gr',
  'e-themis.gr', 'www.e-themis.gr',
  'areiospagos.gr', 'www.areiospagos.gr',
  'ste.gr', 'www.ste.gr',
  'ofae.gr', 'www.ofae.gr',
  'e-nomothesia.gr', 'www.e-nomothesia.gr',
]);

function isTrustedUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (TRUSTED_DOMAINS.has(hostname)) return true;
    for (const domain of TRUSTED_DOMAINS) {
      if (hostname.endsWith('.' + domain.replace(/^www\./, ''))) return true;
    }
    return false;
  } catch {
    return false;
  }
}

function labelFromUrl(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    const path = u.pathname.replace(/\/$/, '');
    const segments = path.split('/').filter(Boolean);
    const last = segments[segments.length - 1] ?? '';
    if (last) return `${host} › ${decodeURIComponent(last).replace(/-/g, ' ')}`;
    return host;
  } catch {
    return url;
  }
}

function buildLawUrl(lawNumber: string, year: string): string {
  const knownLaws: Record<string, string> = {
    '4172/2013': 'https://www.kodiko.gr/nomothesia/document/250460/nomos-4172-2013',
    '4174/2013': 'https://www.kodiko.gr/nomothesia/document/251890/nomos-4174-2013',
    '4308/2014': 'https://www.kodiko.gr/nomothesia/document/99608/nomos-4308-2014',
    '4987/2022': 'https://www.kodiko.gr/nomothesia/document/835018/nomos-4987-2022',
    '5104/2024': 'https://www.aade.gr/nomothesia',
    '5144/2024': 'https://www.kodiko.gr/nomothesia/document/1065469/nomos-5144-2024',
    '5246/2025': 'https://www.kodiko.gr/nomothesia/document/1236646/nomos-5246-2025',
  };
  const key = `${lawNumber}/${year}`;
  if (knownLaws[key]) return knownLaws[key];
  return `https://www.kodiko.gr/nomothesia?q=${encodeURIComponent(`${lawNumber}/${year}`)}`;
}

function buildPolUrl(number: string, year: string): string {
  return `https://www.aade.gr/search?q=ΠΟΛ.+${number}%2F${year}`;
}

function buildAadeUrl(number: string, year: string): string {
  const shortYear = year.slice(2);
  return `https://www.aade.gr/egkyklioi-kai-apofaseis/${number}-${shortYear}`;
}

function parseCitations(text: string): Citation[] {
  const seen = new Set<string>();
  const results: Citation[] = [];
  let i = 0;

  const lawMatches = [...text.matchAll(new RegExp(LAW_CITATION_REGEX.source, 'gm'))];
  for (const m of lawMatches) {
    const lawNumber = m[1];
    const year = m[2];
    const full = m[0];
    const articleMatch = full.match(/Άρθρ[οα]\s([\d\w\s.παρ]+),/);
    const article = articleMatch ? articleMatch[1].trim() : '';
    const key = `law-${lawNumber}-${year}-${article}`;
    if (!seen.has(key)) {
      seen.add(key);
      results.push({ id: `cite-${i++}`, text: full, article: article || '—', law: `${lawNumber}/${year}`, url: buildLawUrl(lawNumber, year), type: 'law', sourceLabel: article ? `Ν. ${lawNumber}/${year} Άρθ. ${article}` : `Ν. ${lawNumber}/${year}` });
    }
  }

  const polMatches = [...text.matchAll(new RegExp(POL_CITATION_REGEX.source, 'gm'))];
  for (const m of polMatches) {
    const full = m[0]; const number = m[1]; const year = m[2];
    const key = `pol-${number}-${year}`;
    if (!seen.has(key)) {
      seen.add(key);
      results.push({ id: `cite-${i++}`, text: full, article: '—', law: `ΠΟΛ. ${number}/${year}`, url: buildPolUrl(number, year), type: 'pol', sourceLabel: `ΠΟΛ. ${number}/${year}` });
    }
  }

  const egkMatches = [...text.matchAll(new RegExp(EGK_CITATION_REGEX.source, 'gm'))];
  for (const m of egkMatches) {
    const full = m[0]; const number = m[1]; const year = m[2];
    const key = `egk-${number}-${year}`;
    if (!seen.has(key)) {
      seen.add(key);
      results.push({ id: `cite-${i++}`, text: full, article: '—', law: `ΕΓΚ. ΑΑΔΕ ${number}/${year}`, url: buildAadeUrl(number, year), type: 'egk', sourceLabel: `Εγκύκλιος ΑΑΔΕ ${number}/${year}` });
    }
  }

  const aadeMatches = [...text.matchAll(new RegExp(AADE_CITATION_REGEX.source, 'gm'))];
  for (const m of aadeMatches) {
    const full = m[0]; const number = m[1]; const year = m[2];
    const key = `aade-${number}-${year}`;
    if (!seen.has(key)) {
      seen.add(key);
      results.push({ id: `cite-${i++}`, text: full, article: '—', law: `ΑΑΔΕ ${number}/${year}`, url: buildAadeUrl(number, year), type: 'aade', sourceLabel: `Απόφαση ΑΑΔΕ ${number}/${year}` });
    }
  }

  const sourceMatches = [...text.matchAll(new RegExp(SOURCE_URL_REGEX.source, 'gm'))];
  for (const m of sourceMatches) {
    const unverifiedTag = m[1];
    const url = (m[2] || '').trim().replace(/[.,;)\]"]+$/, '');
    if (!url.startsWith('http')) continue;
    const isUnverified = !!unverifiedTag || !isTrustedUrl(url);
    const key = `source-${url}`;
    if (!seen.has(key)) {
      seen.add(key);
      results.push({ id: `cite-${i++}`, text: `Πηγή: ${isUnverified ? '[UNVERIFIED] ' : ''}${url}`, article: '—', law: labelFromUrl(url), url, type: 'other', sourceLabel: labelFromUrl(url), isUnverified });
    }
  }

  return results;
}

const CITATION_TYPE_STYLES: Record<Citation['type'], { bg: string; icon: string; badge: string }> = {
  law:   { bg: 'bg-blue-100',   icon: 'text-blue-700',   badge: 'badge-blue' },
  pol:   { bg: 'bg-purple-100', icon: 'text-purple-700', badge: 'badge-purple' },
  egk:   { bg: 'bg-amber-100',  icon: 'text-amber-700',  badge: 'badge-amber' },
  aade:  { bg: 'bg-green-100',  icon: 'text-green-700',  badge: 'badge-green' },
  other: { bg: 'bg-gray-100',   icon: 'text-gray-600',   badge: 'badge-gray' },
};

const SUGGESTED_QUESTIONS = [
  'Πώς θα πάρω την απαλλαγή φόρου 0% αν είμαι κάτω των 25 ετών και ξεκίνησα τώρα δουλειά;',
  'Ισχύει η μείωση 30% στο ΦΠΑ για όλα τα νησιά κάτω των 20.000 κατοίκων από φέτος;',
  'Γιατί ο ΕΦΚΑ μου χρεώνει πάλι αύξηση 2,63% στις εισφορές ενώ οι μισθοί δεν ανέβηκαν τόσο;',
  'Τι είναι το νέο Ψηφιακό Δελτίο Αποστολής και από πότε γίνεται υποχρεωτικό για όλους;',
  'Πώς δηλώνω σωστά το εισόδημα από ενοίκια για να μην πέσω στη νέα κλίμακα του 25%;',
  'Μέχρι πότε έχουμε περιθώριο για το B2B e-invoicing πριν αρχίσουν τα πρόστιμα τον Μάιο;',
  'Ποιες δαπάνες με κάρτα δίνουν φέτος τη διπλή έκπτωση φόρου στο Ε1;',
  'Πώς θα δω αν η ΑΑΔΕ έχει προ-συμπληρώσει σωστά τα στοιχεία μου για το 2025;',
  'Τι αλλάζει στη φορολογία για όσους δουλεύουν ως Working Pensioners (εργαζόμενοι συνταξιούχοι);',
  'Μπορώ να πάρω ακόμα την αναστολή ΦΠΑ για αγορά νεόδμητου ακινήτου μέσα στο 2026;',
  'Αν το myDATA έχει λάθος προ-συμπληρωμένα ποσά στο Ε3, φταίω εγώ ή ο λογιστής μου;',
  'Πώς θα αποδείξω ότι τα 920€ κατώτατου μισθού δεν μου κόβουν επίδομα παιδιού;',
  'Ισχύει ότι από τον Μάιο το Ψηφιακό Δελτίο Αποστολής θα ελέγχεται σε πραγματικό χρόνο;',
  'Πώς μπορώ να δηλώσω τα έξοδα σπιτιού αν δουλεύω remote για εταιρεία εξωτερικού;',
  'Δικαιούμαι την έκπτωση 4% αν κάνω τροποποιητική δήλωση μετά τις 30 Απριλίου;',
];

export default function AIChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [citations, setCitations] = useState<Citation[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showMobileSources, setShowMobileSources] = useState(false);
  // Track last completed assistant message for AnswerActions + AdminOverridesBanner
  const [lastUserQuestion, setLastUserQuestion] = useState('');

  const threadRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sessionIdRef = useRef<string>(`session-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);

  const uid = () => Math.random().toString(36).slice(2, 9);

  const scrollToBottom = useCallback(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const validateCitationUrls = useCallback(async (cites: Citation[]) => {
    const urlsToCheck = cites.map(c => c.url).filter(u => u.startsWith('http'));
    if (urlsToCheck.length === 0) return;
    setCitations(prev => prev.map(c => urlsToCheck.includes(c.url) ? { ...c, isValidating: true } : c));

    try {
      const res = await fetch('/api/validate-sources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls: urlsToCheck }),
      });
      if (!res.ok) return;
      const { results } = (await res.json()) as { results: Record<string, boolean> };
      setCitations(prev => prev.map(c => {
        if (!c.url.startsWith('http')) return { ...c, isValidating: false };
        return { ...c, isValidating: false, isUnreachable: results[c.url] === false };
      }));
    } catch {
      setCitations(prev => prev.map(c => ({ ...c, isValidating: false })));
    }
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;
    const userMsg: ChatMessage = { id: uid(), role: 'user', content: text.trim() };
    const assistantId = uid();
    const assistantMsg: ChatMessage = { id: assistantId, role: 'assistant', content: '' };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setInput('');
    setIsStreaming(true);
    setCitations([]);
    setLastUserQuestion(text.trim());

    const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 60000);

    try {
      // Attach auth token if user is logged in (for question-limit enforcement)
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      const authHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        authHeaders['Authorization'] = `Bearer ${session.access_token}`;
      }

      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ messages: history, sessionId: sessionIdRef.current }),
        signal: abortController.signal,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Σφάλμα διακομιστή' }));
        if (res.status === 429 || errData?.limitReached === true) {
          setMessages(prev => prev.map(m =>
            m.id === assistantId
              ? { ...m, content: 'Έχετε φτάσει το ημερήσιο όριο ερωτήσεών σας. Αναβαθμίστε το πλάνο σας για απεριόριστη πρόσβαση.', isError: true, isLimitReached: true }
              : m
          ));
          setIsStreaming(false);
          clearTimeout(timeoutId);
          return;
        }
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      if (!res.body) throw new Error('Δεν υπάρχει σώμα απόκρισης');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });

        if (chunk.startsWith('{"error"') || chunk.includes('"error":{"message"')) {
          try {
            const errJson = JSON.parse(chunk);
            const errMsg = errJson?.error?.message || errJson?.error || 'OpenAI stream error';
            throw new Error(String(errMsg));
          } catch (parseErr) {
            if (!(parseErr instanceof SyntaxError)) throw parseErr;
          }
        }

        fullText += chunk;
        setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: fullText } : m));
        const liveCitations = parseCitations(fullText);
        if (liveCitations.length > 0) setCitations(liveCitations);
        scrollToBottom();
      }

      const finalCitations = parseCitations(fullText);
      setCitations(finalCitations);
      if (finalCitations.length > 0) validateCitationUrls(finalCitations);

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Άγνωστο σφάλμα';
      (window as Window & { __debugLog?: (a: string, b: string, c: string) => void }).__debugLog?.('AI Chat', message, 'error');
      setMessages(prev => prev.map(m =>
        m.id === assistantId ? { ...m, content: `Σφάλμα: ${message}`, isError: true } : m
      ));
    } finally {
      clearTimeout(timeoutId);
      setIsStreaming(false);
    }
  }, [messages, isStreaming, scrollToBottom, validateCitationUrls]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const copyMessage = async (id: string, content: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getCitationIcon = (type: Citation['type']) => {
    if (type === 'law') return <BookOpen size={14} className={CITATION_TYPE_STYLES[type].icon} />;
    if (type === 'pol' || type === 'egk' || type === 'aade') return <FileText size={14} className={CITATION_TYPE_STYLES[type].icon} />;
    return <Globe size={14} className={CITATION_TYPE_STYLES['other'].icon} />;
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* Chat column */}
      <div className="flex flex-col w-full lg:w-3/5 border-r border-gray-200 bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 bg-white">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Συνομιλία AI</h1>
            <p className="text-xs text-gray-500 mt-0.5">Ελληνική φορολογία 2026</p>
          </div>
          {isStreaming && (
            <div className="flex items-center gap-2 text-blue-700 text-sm">
              <Loader2 size={14} className="animate-spin" />
              <span>Γράφει...</span>
            </div>
          )}
        </div>

        {/* Thread */}
        <div ref={threadRef} className="flex-1 overflow-y-auto scrollbar-thin px-3 sm:px-6 py-4 space-y-4">

          {/* AdminOverridesBanner — shown when active kill-switch overrides match the question */}
          {lastUserQuestion && !isStreaming && (
            <AdminOverridesBanner questionText={lastUserQuestion} />
          )}

          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center py-16 animate-fade-in">
              <Image src="/logo-icon.png" alt="AskΦόρος" width={80} height={80} className="w-20 h-20 rounded-2xl mb-4 mx-auto object-contain" priority unoptimized />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">AskΦόρος</h2>
              <p className="text-gray-500 text-sm max-w-sm mb-6">
                Ρωτήστε οτιδήποτε για την ελληνική φορολογία 2026. Κάθε απάντηση περιλαμβάνει νομικές αναφορές.
              </p>
              {/* Mobile: dropdown */}
              <div className="sm:hidden w-full max-w-2xl">
                <select
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) { sendMessage(e.target.value); e.target.value = ''; } }}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:border-blue-400"
                >
                  <option value="" disabled>Επιλέξτε ερώτηση…</option>
                  {SUGGESTED_QUESTIONS.map((q, i) => (
                    <option key={`suggestion-mobile-${i}`} value={q}>{q}</option>
                  ))}
                </select>
              </div>
              {/* Desktop: grid */}
              <div className="hidden sm:grid sm:grid-cols-2 gap-2 w-full max-w-2xl">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button
                    key={`suggestion-${i}`}
                    onClick={() => sendMessage(q)}
                    className="text-left px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-800 transition-all duration-150"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={`msg-${msg.id}`}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-blue-900 flex items-center justify-center shrink-0 mr-3 mt-1">
                  <span className="text-white text-xs font-bold">AI</span>
                </div>
              )}
              <div className={`max-w-[80%] group`}>
                <div
                  className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user' ? 'bg-blue-900 text-white rounded-tr-sm'
                      : msg.isError
                      ? 'bg-red-50 text-red-800 border border-red-200 rounded-tl-sm' :'bg-white text-gray-800 border border-gray-200 shadow-sm rounded-tl-sm'
                  }`}
                >
                  {msg.isError && (
                    <div className="flex items-center gap-1.5 mb-2 text-red-600 font-medium">
                      <AlertCircle size={14} />
                      <span>Σφάλμα API</span>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  {msg.isLimitReached && (
                    <Link
                      href="/pricing"
                      className="inline-flex items-center gap-1 mt-2 text-xs text-blue-700 font-medium hover:underline"
                    >
                      Δείτε τα πλάνα →
                    </Link>
                  )}
                  {msg.role === 'assistant' && !msg.content && !msg.isError && (
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  )}
                </div>

                {/* ── AnswerActions: flag, save, export, copy — plus disclaimer ── */}
                {msg.role === 'assistant' && msg.content && !msg.isError && (
                  <AnswerActions
                    question={lastUserQuestion}
                    answer={msg.content}
                    citations={citations.map(c => ({ sourceLabel: c.sourceLabel, url: c.url, text: c.text }))}
                    sessionId={sessionIdRef.current}
                    messageId={msg.id}
                  />
                )}

                {/* Legacy copy button — keep visible on hover for quick plain copy */}
                {msg.role === 'assistant' && msg.content && !msg.isError && (
                  <button
                    onClick={() => copyMessage(msg.id, msg.content)}
                    className="mt-0.5 flex items-center gap-1 text-xs text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-all duration-150"
                  >
                    {copiedId === msg.id ? <><Check size={11} /> Αντιγράφηκε</> : <><Copy size={11} /> Απλή αντιγραφή</>}
                  </button>
                )}

                {/* Mobile sources pill */}
                {msg.role === 'assistant' && msg.content && !msg.isError && citations.length > 0 && (
                  <button
                    onClick={() => setShowMobileSources(true)}
                    className="lg:hidden mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <BookOpen size={11} />
                    {citations.length} πηγ{citations.length === 1 ? 'ή' : 'ές'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-white">
          <div className="flex gap-3 items-end">
            <div className="flex-1 relative">
              <textarea
                id="tour-chat-input"
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isStreaming}
                placeholder="Ρωτήστε για ελληνική φορολογία 2026..."
                rows={1}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all duration-150 disabled:bg-gray-50 disabled:text-gray-400 scrollbar-thin"
              />
              <span className="absolute bottom-2 right-3 text-xs text-gray-300 hidden sm:block">
                Enter για αποστολή
              </span>
            </div>
            <button
              onClick={() => sendMessage(input)}
              disabled={isStreaming || !input.trim()}
              className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-blue-900 text-white rounded-xl text-sm font-medium hover:bg-blue-800 active:scale-95 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              {isStreaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              <span className="hidden sm:inline">Αποστολή</span>
            </button>
          </div>
        </div>
      </div>

      {/* Citations sidebar — desktop */}
      <div id="tour-citations" className="hidden lg:flex flex-col w-2/5 bg-gray-50">
        <div className="px-6 py-4 border-b border-gray-200 bg-white">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen size={16} className="text-blue-700" />
            Αναφορές
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {citations.length > 0
              ? `${citations.length} αναφορ${citations.length === 1 ? 'ά' : 'ές'} · επαληθεύονται αυτόματα`
              : 'Κάντε κλικ για να ανοίξετε την επίσημη πηγή'}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-4">
          {citations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <BookOpen size={32} className="text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-400 mb-1">Νομικές Αναφορές</p>
              <p className="text-xs text-gray-400 max-w-[200px]">
                Οι αναφορές θα εμφανιστούν εδώ κατά τη διάρκεια κάθε απάντησης
              </p>
            </div>
          ) : (
            <div className="space-y-3 animate-fade-in">
              {citations.map((cite) => {
                const style = CITATION_TYPE_STYLES[cite.type];
                const isDisabled = cite.isUnreachable === true;
                return (
                  <a
                    key={cite.id}
                    href={isDisabled ? undefined : cite.url}
                    target={isDisabled ? undefined : '_blank'}
                    rel="noopener noreferrer"
                    onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                    className={`block bg-white rounded-lg border p-4 shadow-sm transition-all duration-150 group ${
                      isDisabled
                        ? 'border-red-200 opacity-60 cursor-not-allowed'
                        : cite.isUnverified
                        ? 'border-amber-300 hover:border-amber-400 hover:shadow-md cursor-pointer'
                        : 'border-gray-200 hover:border-blue-300 hover:shadow-md cursor-pointer'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg ${style.bg} flex items-center justify-center shrink-0`}>
                        {getCitationIcon(cite.type)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-1 mb-1.5">
                          <div className="flex flex-wrap gap-1.5 items-center">
                            {/* Validating spinner */}
                            {cite.isValidating && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                                <Loader2 size={10} className="animate-spin" />
                                Έλεγχος...
                              </span>
                            )}
                            {/* Unreachable badge */}
                            {!cite.isValidating && isDisabled && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                                <AlertCircle size={10} />
                                Μη προσβάσιμη
                              </span>
                            )}
                            {/* Unverified domain badge */}
                            {!cite.isValidating && !isDisabled && cite.isUnverified && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                                <HelpCircle size={11} />
                                Μη επαληθευμένη
                              </span>
                            )}
                            {cite.type === 'law' && cite.article !== '—' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Άρθρο {cite.article}
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              cite.type === 'law'  ? 'bg-gray-100 text-gray-700' :
                              cite.type === 'pol'  ? 'bg-purple-100 text-purple-800' :
                              cite.type === 'egk'  ? 'bg-amber-100 text-amber-800' :
                              cite.type === 'aade' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {cite.sourceLabel}
                            </span>
                          </div>
                          {!isDisabled && (
                            <ExternalLink size={12} className="text-gray-300 group-hover:text-blue-500 shrink-0 transition-colors" />
                          )}
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {cite.isUnverified
                            ? cite.text.replace('[UNVERIFIED] ', '')
                            : cite.text}
                        </p>
                        <p className={`text-xs mt-1.5 truncate ${
                          isDisabled
                            ? 'text-red-400 line-through'
                            : cite.isUnverified
                            ? 'text-amber-600 group-hover:text-amber-700' :'text-blue-600 group-hover:text-blue-700'
                        }`}>
                          {cite.url}
                        </p>
                      </div>
                    </div>
                  </a>
                );
              })}
              <button
                onClick={() => setCitations([])}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mt-2 transition-colors"
              >
                <RefreshCw size={11} />
                Εκκαθάριση αναφορών
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Floating mobile sources button */}
      {citations.length > 0 && (
        <button
          onClick={() => setShowMobileSources(true)}
          className="lg:hidden fixed bottom-24 right-4 z-40 flex items-center gap-2 px-4 py-2.5 bg-blue-900 text-white rounded-full text-sm font-medium shadow-lg"
        >
          <BookOpen size={14} />
          Πηγές ({citations.length})
        </button>
      )}

      {/* Mobile sources drawer */}
      {showMobileSources && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowMobileSources(false)}
          />
          <div className="relative bg-white rounded-t-2xl max-h-[75vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <BookOpen size={15} className="text-blue-700" />
                Νομικές Αναφορές ({citations.length})
              </h3>
              <button
                onClick={() => setShowMobileSources(false)}
                className="text-gray-400 hover:text-gray-700 p-1"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
              {citations.map((cite) => {
                const style = CITATION_TYPE_STYLES[cite.type];
                const isDisabled = cite.isUnreachable === true;
                return (
                  <a
                    key={`mobile-${cite.id}`}
                    href={isDisabled ? undefined : cite.url}
                    target={isDisabled ? undefined : '_blank'}
                    rel="noopener noreferrer"
                    onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                    className={`block bg-gray-50 rounded-lg border p-3 text-sm ${
                      isDisabled ? 'border-red-200 opacity-60 cursor-not-allowed' :'border-gray-200 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-6 h-6 rounded ${style.bg} flex items-center justify-center shrink-0`}>
                        {getCitationIcon(cite.type)}
                      </div>
                      <span className="font-medium text-gray-800 text-xs">{cite.sourceLabel}</span>
                      {!isDisabled && <ExternalLink size={11} className="text-gray-400 ml-auto" />}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{cite.url}</p>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}